const HISTORY_REPLACE_LIMIT = 20;
const HISTORY_REPLACE_WINDOW_MS = 10_000;

let historyReplaceWindowStartedAt = 0;
let historyReplaceCount = 0;

export type SearchParamReplaceContext = {
  actionName: string;
  requestId?: string | null;
  tab?: string | null;
};

type SetSearchParams = (
  nextInit: URLSearchParams,
  navigateOptions?: { replace?: boolean },
) => void;

export function resetHistoryReplaceBudget(): void {
  historyReplaceWindowStartedAt = 0;
  historyReplaceCount = 0;
}

export function consumeHistoryReplaceBudget(now = Date.now()): boolean {
  if (now - historyReplaceWindowStartedAt >= HISTORY_REPLACE_WINDOW_MS) {
    historyReplaceWindowStartedAt = now;
    historyReplaceCount = 0;
  }
  if (historyReplaceCount >= HISTORY_REPLACE_LIMIT) return false;
  historyReplaceCount += 1;
  return true;
}

const logReplaceFailure = (
  message: string,
  context: SearchParamReplaceContext,
  detail?: string,
) => {
  console.error(message, {
    actionName: context.actionName,
    recoverable: true,
    requestId: context.requestId ?? null,
    tab: context.tab ?? null,
    message: detail ?? message,
  });
};

/**
 * Writes search params with history.replaceState at most once per distinct query,
 * and stops before the browser's replaceState rate limit. Failures stay on the
 * current page and are logged as recoverable.
 */
export function replaceSearchParamsSafely(
  setSearchParams: SetSearchParams | null | undefined,
  current: URLSearchParams | null | undefined,
  next: URLSearchParams | null | undefined,
  context: SearchParamReplaceContext,
): boolean {
  if (!(next instanceof URLSearchParams)) {
    logReplaceFailure(
      '[Events] Missing search params for history.replaceState',
      context,
      'next search params were null or undefined',
    );
    return false;
  }

  const currentParams = current instanceof URLSearchParams ? current : new URLSearchParams();
  if (next.toString() === currentParams.toString()) return false;

  if (typeof setSearchParams !== 'function') {
    logReplaceFailure(
      '[Events] Could not update the events URL',
      context,
      'setSearchParams is unavailable',
    );
    return false;
  }

  if (!consumeHistoryReplaceBudget()) {
    logReplaceFailure(
      '[Events] Stopped updating the events URL after too many history.replaceState calls',
      context,
    );
    return false;
  }

  try {
    setSearchParams(next, { replace: true });
    return true;
  } catch (error) {
    logReplaceFailure(
      '[Events] history.replaceState failed',
      context,
      error instanceof Error ? error.message : String(error),
    );
    return false;
  }
}
